import React, {ReactElement, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm, Controller, SubmitHandler} from 'react-hook-form';
import {validateWithHookForm} from '@shared/modules/public/forgotpassword/ForgotPassword';
import {forgotPasswordService} from '@shared/services/PublicService';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  makeStyles,
  LinearProgress,
  createStyles,
  Theme
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
// import Loader from '@components/framework/loader/Loader';
import './ForgotPassword.scss';
import NavRoutes from '../../../constants/route/NavRoutes';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import {
  FONT_SECONDARY_COLOR,
  BORDER_PRIMARY_COLOR,
  APP
} from 'src/style/variable';
// import VaccineTrackLogoBlue from '@components/system/MyIcons/VaccineTrackLogoBlue';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import LogoIcon from '../login/LogoIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    forgotPasswordBox: {
      display: 'flex',
      justifyContent: 'center',
      width: '50%',
      flexDirection: 'column',
      '@media (min-width: 0px) and (max-width: 1023px)': {
        width: '100%'
      }
    },
    logo: {
      '@media (min-width: 0px) and (max-width: 1023px) and (min-height: 500px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '40px'
      },
      '@media (min-height: 0px) and (max-height: 499px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '0px'
      },
      '@media (min-width: 1024px)': {
        display: 'none'
      }
    },
    FormLabel: {
      left: '-0.8rem',
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    appBar: {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    forgotButton: {
      justifyContent: 'center',
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center',
      paddingBottom: '1.2rem'
    },
    formWrapper: {
      // borderRadius: 0,
      marginTop: '1rem !important',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: BORDER_PRIMARY_COLOR,
        top: 0,
        '& legend': {
          display: 'none'
        }
      }
    },
    button: {
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '2.7rem',
      justifyContent: 'center',
      padding: 0,
      '& .span': {
        padding: 0
      }
    },
    forgotTitle: {
      display: 'flex',
      whiteSpace: 'nowrap',
      left: 0,
      paddingBottom: '2.5rem',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontSize: '1.7rem',
      '@media (min-width: 0px) and (max-width: 300px)': {
        fontSize: '1.3rem'
      }
    },
    copyRightText: {
      fontSize: '1rem'
    }
  })
);

const ForgotPassword = ({history}: any): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const {control, handleSubmit, errors} = useForm<{email: string}>({
    mode: 'all',
    resolver: async values => {
      return {values, errors: validateWithHookForm(values)};
    }
  });

  const [formState, setFormState] = useState({
    loading: false
  });

  const redirectToLogin = () => {
    history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
  };

  // Synchronous validation is being implemented here
  const handleFormSubmit: SubmitHandler<{email: string}> = async values => {
    setFormState({...formState, loading: true});
    try {
      const res = await forgotPasswordService(values);
      enqueueSnackbar(
        res && res.message ? res.message : 'Error encountered',
        SnackBarConfig('s')
      );
      redirectToLogin();
    } catch (e) {
      enqueueSnackbar(
        e && e.message ? e.message : 'Error encountered',
        SnackBarConfig('e')
      );
      setFormState({...formState, loading: false});
    }
  };
  const renderLogo = (): ReactElement => {
    return (
      <div className={classes.logo}>
        <MedopsLogo style={{height: '100%', width: '30%'}} />
      </div>
    );
  };
  return (
    <div className="forgotpass-wrapper" data-test-id="pcl-login-forgotPassPage">
      {formState.loading && (
        <LinearProgress style={{borderTopRightRadius: 5}} color="primary" />
      )}
      <div className="forgotpass-content-wrap">
        <LogoIcon />
        <div className={classes.forgotPasswordBox}>
          {renderLogo()}
          <div className="card-wrapper">
            <div className="forgotpass-form">
              <Typography className={classes.forgotTitle} variant={'h3'}>
                {t('FORGOT_PASSWORD.TITLE')}
              </Typography>
              <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div>
                  <FormControl
                    error={!!errors.email}
                    fullWidth
                    variant="outlined">
                    <InputLabel shrink className={classes.FormLabel}>
                      {t('LOGIN.EMAIL')}
                    </InputLabel>
                    <Controller
                      id="outlined-adornment-email"
                      as={StyledOutlinedInput}
                      className={classes.formWrapper}
                      margin={'dense'}
                      control={control}
                      name={'email'}
                      type={'text'}
                      autoComplete="on"
                      placeholder={t('FORGOT_PASSWORD.EMAIL')}
                      labelWidth={0}
                      defaultValue=""
                    />
                    {errors?.email && errors?.email.message && (
                      <FormHelperText data-test-id="pcl-login-invalidMail">
                        {t(errors.email?.message)}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div className={classes.forgotButton}>
                  <Button
                    variant="contained"
                    size={'medium'}
                    color="primary"
                    className={['block', classes.button].join(' ')}
                    disabled={formState.loading}
                    onClick={handleSubmit(handleFormSubmit)}>
                    {t('FORGOT_PASSWORD.SEND_LINK')}
                  </Button>
                </div>
                <div
                  className="forgot-login-link"
                  data-test-id="pcl-login-backLoginLink">
                  <Typography
                    className="login"
                    variant={'subtitle2'}
                    onClick={redirectToLogin}>
                    {t('FORGOT_PASSWORD.SIGN_IN')}
                  </Typography>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
